import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { CMSGroupMode } from '@/api/cms/base';
import {
  getWpBonusesCMSUrl,
  type WpBonusGroup,
} from '@/api/cms/progressiveBonuses';
import { parseGroup } from '@/helpers/cms';
import { groupMemberTEST, USER_GROUP } from '@/helpers/user';
import { fetcherCMS } from '@/modules/api';
import { useMatchingSections } from '@/modules/restrictions/hooks/useAccessSections';
import { useGetCommonStore } from '@/store';

export const useCMSProgressiveBonuses = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const isTestUser = useMemo(() => groupMemberTEST(user), [user]);

  const url = useMemo(() => getWpBonusesCMSUrl(language), [language]);

  const {
    data = [],
    error,
    isLoading,
  } = useSWRImmutable<WpBonusGroup[]>(url, fetcherCMS);

  // AND-mode sections can't list TEST without forcing testers to match every other
  // group too, so bypass include for them there; OR-mode CMS can just add TEST itself.
  const bypassIncludeForTest = useCallback(
    (section: WpBonusGroup) =>
      isTestUser &&
      section.groupsIncludeMode === CMSGroupMode.AND &&
      !parseGroup(section.groupsExclude).includes(USER_GROUP.TEST),
    [isTestUser]
  );

  const bonusGroups = useMatchingSections(data, bypassIncludeForTest);

  return {
    data,
    isLoading,
    isError: error,
    bonusGroups,
  };
};
